import { Bell, ChevronDown, ChevronRight, LogOut, Menu, Search, Settings, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCourseSignups, getNotificationReadIds, getTransactions } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { buildDashboardNotifications } from '../../utils/dashboardNotifications';

function initial(value) {
  return (value || 'U').trim().charAt(0).toUpperCase();
}

export function Topbar({ title, navigationOpen, onMenuClick }) {
  const { user, logout } = useAuth();
  const { activeTenant } = useTenant();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    async function loadNotifications() {
      if (!activeTenant?.id) return;
      try {
        const [signups, transactions] = await Promise.all([getCourseSignups(activeTenant.id), getTransactions(activeTenant.id)]);
        let readIds = [];
        try {
          readIds = await getNotificationReadIds(user?.uid, activeTenant.id);
        } catch (error) {
          console.warn('Unable to load notification read status. Deploy the latest Firestore rules to enable sync.', error);
        }
        const notifications = buildDashboardNotifications(signups, transactions);
        if (mounted) setUnreadCount(notifications.filter((item) => !readIds.includes(item.id)).length);
      } catch (error) {
        console.error('Unable to load dashboard notifications', error);
      }
    }
    loadNotifications();
    const handleNotificationRead = (event) => {
      if (event.detail?.tenantId === activeTenant?.id) loadNotifications();
    };
    window.addEventListener('dashboard-notification-read', handleNotificationRead);
    const interval = window.setInterval(loadNotifications, 30000);
    return () => {
      mounted = false;
      window.clearInterval(interval);
      window.removeEventListener('dashboard-notification-read', handleNotificationRead);
    };
  }, [activeTenant?.id, user?.uid]);

  return (
    <header className="topbar">
      <div className="topbar-title">
        <button className="icon-button mobile-menu topbar-mobile-menu" type="button" onClick={onMenuClick} aria-label="Open navigation" aria-controls="dashboard-navigation" aria-expanded={navigationOpen}>
          <Menu size={20} />
        </button>
        <span className="topbar-workspace-mark">{initial(activeTenant?.name)}</span>
        <div className="topbar-title-copy">
          <h1>{activeTenant?.name || 'Your workspace'}</h1>
          <span>{title}</span>
        </div>
      </div>
      <div className="topbar-actions">
        <Link className="icon-button topbar-icon-button topbar-notification-button" to="/dashboard/notifications" aria-label="Open notifications"><Bell size={18} />{unreadCount > 0 ? <span className="topbar-notification-count">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}</Link>
        <div className="user-menu">
          <button className="user-menu-trigger" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-haspopup="menu">
            <span className="user-avatar">{initial(user?.displayName || user?.email)}</span>
            <span className="user-menu-copy"><strong>{user?.displayName || 'My account'}</strong><small>Account</small></span>
            <ChevronDown size={16} />
          </button>
          {open ? (
            <div className="dropdown-menu dashboard-user-dropdown" role="menu">
              <Link className="dashboard-user-dropdown-item" to="/dashboard/settings" onClick={() => setOpen(false)} role="menuitem">
                <span className="dashboard-user-dropdown-item-icon"><UserRound size={15} /></span>
                <span><strong>Profile</strong><small>Set up your profile</small></span>
                <ChevronRight size={15} />
              </Link>
              <Link className="dashboard-user-dropdown-item" to="/dashboard/settings" onClick={() => setOpen(false)} role="menuitem">
                <span className="dashboard-user-dropdown-item-icon"><Settings size={15} /></span>
                <span><strong>Account settings</strong><small>Manage your workspace</small></span>
                <ChevronRight size={15} />
              </Link>
              <div className="dashboard-user-dropdown-divider" />
              <button className="dashboard-user-dropdown-logout" type="button" onClick={logout} role="menuitem">
                <span className="dashboard-user-dropdown-item-icon"><LogOut size={15} /></span>
                <span>Sign out</span>
              </button>
            </div>
          ) : null}
        </div>
        <label className="search-box">
          <Search size={17} />
          <input placeholder="Start searching here ..." aria-label="Search your workspace" />
        </label>
      </div>
    </header>
  );
}
